import type { Header, RowData } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "@/utils/cx";
import type { AppTableFeatures } from "./app-table-features";

interface DeferredColumnResizerProps<TData extends RowData> {
	header: Header<AppTableFeatures, TData, unknown>;
	/** Reference to the table container for positioning the indicator */
	tableContainerRef: React.RefObject<HTMLElement | null>;
	/** Callback when resize is committed (on mouseup) */
	onResizeCommit: (columnId: string, newWidth: number) => void;
	/** Double-click: restore auto width + max-width for this column */
	onResizeReset?: (columnId: string) => void;
	/** Minimum column width */
	minWidth?: number;
	/**
	 * Last leaf column — keep the handle flush inside the cell so it does not
	 * extend past the table edge and create empty horizontal scroll.
	 */
	flushEnd?: boolean;
}

function measureHeaderWidth(
	eventTarget: EventTarget | null,
	fallback: number,
): number {
	const el = eventTarget instanceof Element ? eventTarget : null;
	const headerCell = el?.closest("th");
	const measured = headerCell?.getBoundingClientRect().width;
	return measured && measured > 0 ? measured : fallback;
}

/**
 * DeferredColumnResizer - A column resize handle that doesn't trigger re-renders during drag.
 *
 * Instead of using TanStack's built-in resize handler (which updates state on every mousemove),
 * this component:
 * 1. Tracks the resize delta locally using refs (no state updates = no re-renders)
 * 2. Shows a visual indicator line where the new column edge will be
 * 3. Only commits the new width on mouseup
 */
export function DeferredColumnResizer<TData extends RowData>({
	header,
	tableContainerRef,
	onResizeCommit,
	onResizeReset,
	minWidth = 50,
	flushEnd = false,
}: DeferredColumnResizerProps<TData>) {
	// Use refs to track resize state without causing re-renders
	const isResizingRef = useRef(false);
	const startXRef = useRef(0);
	const startWidthRef = useRef(0);
	const currentDeltaRef = useRef(0);

	// Only this state triggers re-renders - controls indicator visibility
	const [isResizing, setIsResizing] = useState(false);
	const [indicatorPosition, setIndicatorPosition] = useState<{
		left: number;
		top: number;
		height: number;
	} | null>(null);

	// Get the column's current width
	const columnWidth = header.getSize();

	// Store the initial right edge position of the column being resized
	const initialRightEdgeRef = useRef(0);

	// Calculate indicator position - uses the initial right edge + delta
	const updateIndicatorPosition = useCallback(
		(delta: number, mouseX?: number) => {
			const tableContainer = tableContainerRef.current;
			if (!tableContainer) return;

			const tableRect = tableContainer.getBoundingClientRect();

			// If we have the mouse X position, use it directly for the indicator
			if (mouseX !== undefined) {
				// Constrain to minimum width from the start position
				const minLeft =
					initialRightEdgeRef.current - startWidthRef.current + minWidth;
				setIndicatorPosition({
					left: Math.max(minLeft, mouseX),
					top: tableRect.top,
					height: tableRect.height,
				});
				return;
			}

			// Fallback: use initial position + delta
			const newLeft = initialRightEdgeRef.current + delta;
			const minLeft =
				initialRightEdgeRef.current - startWidthRef.current + minWidth;

			setIndicatorPosition({
				left: Math.max(minLeft, newLeft),
				top: tableRect.top,
				height: tableRect.height,
			});
		},
		[tableContainerRef, minWidth],
	);

	// Handle mousedown - start tracking resize
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			isResizingRef.current = true;
			startXRef.current = e.clientX;
			startWidthRef.current = measureHeaderWidth(e.target, columnWidth);
			currentDeltaRef.current = 0;

			// Store the initial right edge position (where the resize handle is)
			initialRightEdgeRef.current = e.clientX;

			setIsResizing(true);
			updateIndicatorPosition(0, e.clientX);

			// Add cursor style to body during resize
			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
		},
		[columnWidth, updateIndicatorPosition],
	);

	// Handle touch start for mobile
	const handleTouchStart = useCallback(
		(e: React.TouchEvent) => {
			const touch = e.touches[0];
			if (!touch) return;

			e.preventDefault();

			isResizingRef.current = true;
			startXRef.current = touch.clientX;
			startWidthRef.current = measureHeaderWidth(e.target, columnWidth);
			currentDeltaRef.current = 0;

			// Store the initial right edge position
			initialRightEdgeRef.current = touch.clientX;

			setIsResizing(true);
			updateIndicatorPosition(0, touch.clientX);
		},
		[columnWidth, updateIndicatorPosition],
	);

	// Set up document-level mousemove and mouseup handlers
	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isResizingRef.current) return;

			const delta = e.clientX - startXRef.current;
			currentDeltaRef.current = delta;

			// Update indicator position using mouse X directly
			updateIndicatorPosition(delta, e.clientX);
		};

		const handleMouseUp = () => {
			if (!isResizingRef.current) return;

			// Calculate final width
			const delta = currentDeltaRef.current;
			const newWidth = Math.max(minWidth, startWidthRef.current + delta);

			// Reset state
			isResizingRef.current = false;
			startXRef.current = 0;
			startWidthRef.current = 0;
			currentDeltaRef.current = 0;
			initialRightEdgeRef.current = 0;

			setIsResizing(false);
			setIndicatorPosition(null);

			// Reset cursor
			document.body.style.cursor = "";
			document.body.style.userSelect = "";

			// Commit the resize
			onResizeCommit(header.column.id, newWidth);
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (!isResizingRef.current) return;

			const touch = e.touches[0];
			if (!touch) return;

			const delta = touch.clientX - startXRef.current;
			currentDeltaRef.current = delta;

			updateIndicatorPosition(delta, touch.clientX);
		};

		const handleTouchEnd = () => {
			handleMouseUp();
		};

		// Add listeners
		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
		document.addEventListener("touchmove", handleTouchMove, { passive: false });
		document.addEventListener("touchend", handleTouchEnd);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.removeEventListener("touchmove", handleTouchMove);
			document.removeEventListener("touchend", handleTouchEnd);
		};
	}, [header.column.id, minWidth, onResizeCommit, updateIndicatorPosition]);

	// Double-click to restore auto + max-width for this column
	const handleDoubleClick = useCallback(() => {
		header.column.resetSize();
		onResizeReset?.(header.column.id);
	}, [header, onResizeReset]);

	return (
		<>
			{/* Resize handle button */}
			<button
				type="button"
				aria-label="Resize column"
				onDoubleClick={handleDoubleClick}
				onMouseDown={handleMouseDown}
				onTouchStart={handleTouchStart}
				className={cx(
					"absolute top-0 right-0 h-full w-2 cursor-col-resize select-none touch-none border-0 bg-transparent p-0",
					"opacity-0 hover:opacity-100 group-hover/header:opacity-100",
					"hover:bg-brand-solid",
					isResizing && "opacity-100 bg-brand-solid",
				)}
				style={{ transform: flushEnd ? undefined : "translateX(50%)" }}
			/>

			{/* Resize indicator line - rendered in a portal */}
			{isResizing &&
				indicatorPosition &&
				createPortal(
					<div
						className="pointer-events-none fixed z-[9999]"
						style={{
							left: indicatorPosition.left,
							top: indicatorPosition.top,
							height: indicatorPosition.height,
							width: 2,
						}}
					>
						<div className="h-full w-full bg-brand-solid" />
					</div>,
					document.body,
				)}
		</>
	);
}
